import { ErrorCode } from '@ps/web-core-enums';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { StatusCode } from '@swf/core/enums/statusCode';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import { type Dispatch, select } from '@swf/core/store';
import type { Response } from '@swf/core/types/Account';

interface LocationState {
  extAuthToken?: string;
  error?: string;
  mitIdAuth?: string;
  buffer?: string;
  flowId?: string;
}

const useMitId = <T extends { extAuthToken?: string }>(
  effect?: (values: T) => Promise<Response>
) => {
  const isEmbedded = useSelector(select.device.isEmbedded);
  const fromEvent = useFromEvent();
  const { state: locationState } = useLocation<LocationState>();
  const { modal } = useDispatch<Dispatch>();
  const [loading, setLoading] = useState(false);
  const [mitIdData, setMitIdData] = useState<T>();
  const [formValues, setFormValues] = useState<T>();
  const [isMitIdRequired, setIsMitIdRequired] = useState(false);
  const [isEmbeddedMitIdRequired, setIsEmbeddedMitIdRequired] = useState(false);
  const [mitIdError, setMitIdError] = useState(false);

  const handleError = useCallback(
    (values: T) => {
      setFormValues(values);
      if (isEmbedded) {
        setIsEmbeddedMitIdRequired(true);
        window.starsweb?.mitid?.requestToken?.();
      } else {
        setIsMitIdRequired(true);
      }
    },
    [isEmbedded]
  );

  const mitIdSubmit = async (values: T) => {
    setLoading(true);
    const response = (await effect?.(values)) as Response;
    setLoading(false);

    if (
      response.errors?.code === StatusCode.MITID_AUTH_REQUIRED ||
      response.errors?.code === ErrorCode.MITID_AUTH_REQUIRED
    ) {
      handleError(values);
      return;
    }

    setMitIdData(undefined);

    return response;
  };

  const starsMitIdToken = useCallback(() => {
    if (window.starsweb?.mitid?.token) {
      setMitIdError(false);
      setIsEmbeddedMitIdRequired(false);

      return setMitIdData({
        extAuthToken: window.starsweb?.mitid?.token as string,
        ...formValues
      } as T);
    }
    setMitIdError(true);
  }, [formValues]);

  useEffect(() => {
    if (locationState?.mitIdAuth === 'true') {
      const localStorageData = localStorage.getItem('mitId');
      const dec = new TextDecoder();
      localStorage.removeItem('mitId');
      if (localStorageData) {
        const bufferStored = locationState.buffer as string;
        const { flowId: flowIdStored, privateKey: privateKeyStored } = JSON.parse(
          localStorageData as string
        );

        if (locationState.flowId === flowIdStored) {
          window.crypto.subtle
            .importKey(
              'jwk',
              JSON.parse(privateKeyStored),
              {
                name: 'RSA-OAEP',
                hash: 'SHA-256'
              },
              true,
              ['decrypt']
            )
            .then((privateKeyImported) => {
              const textToDecrypt = new Uint8Array(
                bufferStored.split(',') as unknown as ArrayBuffer
              );
              window.crypto.subtle
                .decrypt(
                  {
                    name: 'RSA-OAEP'
                  },
                  privateKeyImported,
                  textToDecrypt
                )
                .then((decrypted) => {
                  modal.replace([
                    {
                      path: '/auth/login',
                      locationState: {
                        ...JSON.parse(dec.decode(decrypted)),
                        extAuthToken: locationState?.extAuthToken,
                        error: locationState?.error
                      }
                    }
                  ]);
                });
            });
        }
      }
    } else {
      if (locationState?.error) {
        setMitIdError(true);
      } else if (locationState?.extAuthToken) {
        setMitIdData(locationState as T);
      }
    }
  }, [locationState, mitIdError, modal]);

  useEffect(() => {
    if (isEmbedded) {
      fromEvent(document, 'starsmitidtoken').subscribe(() => starsMitIdToken());
    }
  }, [fromEvent, isEmbedded, starsMitIdToken]);

  return {
    loading,
    mitIdError,
    mitIdData,
    isMitIdRequired: isEmbedded ? isEmbeddedMitIdRequired : isMitIdRequired,
    mitIdSubmit
  };
};

export default useMitId;
